import { useState, useRef } from 'react'
import { Repeat, Check, X } from 'lucide-react'
import { useClickOutside } from '@/lib/useClickOutside'
import type { Recurrence } from '@/lib/types'

export const RECUR_LABEL: Record<Recurrence, string> = {
  daily: 'Harian',
  weekly: 'Mingguan',
  monthly: 'Bulanan',
}

const OPTIONS: Recurrence[] = ['daily', 'weekly', 'monthly']

// Pill warna teal supaya beda jelas dari status (hijau/kuning/abu).
const PILL = 'bg-[#CFFAFE] text-[#0E7490] border-[#A5F3FC]'

/** Badge read-only untuk menandai item rutin. */
export function RecurrenceBadge({ value, size = 'sm' }: { value: Recurrence; size?: 'sm' | 'xs' }) {
  const pad = size === 'xs' ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-0.5 text-[10px]'
  return (
    <span className={`inline-flex items-center gap-1 rounded-md font-body font-bold uppercase tracking-wider border ${pad} ${PILL}`}>
      <Repeat className="w-2.5 h-2.5" />
      {RECUR_LABEL[value]}
    </span>
  )
}

/**
 * Kontrol untuk menandai task/subtask jadi rutin (daily/weekly/monthly) atau melepas.
 * - value terisi → pill "↻ <Label>" yang bisa diklik untuk ubah/lepas.
 * - value null   → tombol ikon Repeat (muted) untuk mengaktifkan.
 * - disabled → render badge/ikon tanpa dropdown (read-only).
 */
export function RecurrenceControl({
  value,
  onChange,
  disabled = false,
  size = 'sm',
}: {
  value: Recurrence | null | undefined
  onChange: (v: Recurrence | null) => void
  disabled?: boolean
  size?: 'sm' | 'xs'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  if (disabled) {
    return value ? <RecurrenceBadge value={value} size={size} /> : null
  }

  const pick = (v: Recurrence | null) => { onChange(v); setOpen(false) }
  const pad = size === 'xs' ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-0.5 text-[10px]'

  return (
    <div ref={ref} className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      {value ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Ubah / lepas pengulangan"
          className={`inline-flex items-center gap-1 rounded-md font-body font-bold uppercase tracking-wider border cursor-pointer hover:brightness-95 ${pad} ${PILL}`}
        >
          <Repeat className="w-2.5 h-2.5" />
          {RECUR_LABEL[value]}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Jadikan tugas rutin"
          aria-label="Jadikan tugas rutin"
          className="p-1 rounded-md text-[#C8C7BF] hover:text-[#0E7490] hover:bg-[#ECFEFF] transition-colors"
        >
          <Repeat className="w-3.5 h-3.5" />
        </button>
      )}

      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 w-40 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 animate-fade-in">
          <p className="px-3 py-1 text-[9px] uppercase tracking-wider text-[#AEADA8] font-body font-semibold">Tugas rutin</p>
          {OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => pick(opt)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-body transition-colors ${
                value === opt ? 'bg-[#ECFEFF] text-[#0E7490] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
              }`}
            >
              <Repeat className="w-3 h-3 flex-shrink-0" />
              <span className="flex-1 text-left">{RECUR_LABEL[opt]}</span>
              {value === opt && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
            </button>
          ))}
          {value && (
            <>
              <div className="my-1 border-t border-[#F2F1EC]" />
              <button
                type="button"
                onClick={() => pick(null)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-body text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
              >
                <X className="w-3.5 h-3.5 flex-shrink-0" /> Bukan rutin
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
